import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { ABTestLoadingState } from '@/components/ab-test/ABTestLoadingState';
import { CopyCodeModal } from '@/components/shared/CopyCodeModal';
import { Button } from '@/components/ui/button';
import { Copy } from 'lucide-react';
import { useABTestDetails } from './hooks/useABTestDetails';
import { DateRange } from 'react-day-picker';
import { buildPresetRange } from '@/utils/period-presets';
import { ABTestMetricsHeader } from '@/components/ab-test/metrics/ABTestMetricsHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ABTestMetricsTabContent } from '@/components/ab-test/metrics/tabs/ABTestMetricsTabContent';
import { ABTestRetentionTabContent } from '@/components/ab-test/metrics/tabs/ABTestRetentionTabContent';

// Mock retention data
const mockRetentionData = Array.from({ length: 60 }, (_, i) => {
  const time = i * 5;
  return {
    time,
    '1': Math.max(100 - (i * 1.2) - Math.random() * 5, 30),
    '2': Math.max(100 - (i * 0.9) - Math.random() * 5, 40),
    '3': Math.max(100 - (i * 1.5) - Math.random() * 5, 25),
  };
});

const ABTestDetails = () => {
  useLanguage();
  const { id } = useParams<{ id: string }>();
  const { 
    loading, 
    abTest, 
    handleFinalizeTeste,
    handleTrafficUpdate 
  } = useABTestDetails(id || '');
  
  const [embedModalOpen, setEmbedModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() =>
    buildPresetRange('last30Days', new Date())
  );

  if (loading) {
    return <ABTestLoadingState />;
  }

  const handleBackToList = () => {
    window.history.back();
  };

  return (
    <Layout fixedSidebar>
      <div className="p-6 space-y-6 animate-fade-in max-w-[1280px] mx-auto">
        {abTest && (
          <>
            <ABTestMetricsHeader
              testName={abTest.name}
              testStatus={abTest.status}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              onBackClick={handleBackToList}
            />

            {/* Premium Tabs */}
            <Tabs defaultValue="retention" className="w-full">
              <div className="mb-8 flex justify-start">
                <TabsList className="inline-grid grid-cols-2 h-[46px] bg-white border border-[#E7E9EF] rounded-[12px] p-1">
                  <TabsTrigger 
                    value="retention"
                    className="rounded-[10px] data-[state=active]:bg-[#F3EFFF] data-[state=active]:text-[#5B35FF] text-[#7A7A8B] font-medium transition-all duration-120"
                  >
                    {translateInterfaceText("Retenção")}</TabsTrigger>
                  <TabsTrigger 
                    value="metrics"
                    className="rounded-[10px] data-[state=active]:bg-[#F3EFFF] data-[state=active]:text-[#5B35FF] text-[#7A7A8B] font-medium transition-all duration-120"
                  >
                    {translateInterfaceText("Métricas")}</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="retention" className="mt-0">
                <ABTestRetentionTabContent 
                  videos={abTest.videos} 
                  retentionData={mockRetentionData}
                  winnerVideoId={abTest.videos.find(v => v.isWinning)?.id}
                  winnerVideoName={abTest.videos.find(v => v.isWinning)?.name}
                />
              </TabsContent>

              <TabsContent value="metrics" className="mt-0">
                <ABTestMetricsTabContent 
                  videos={abTest.videos}
                  isEditable={abTest.status === 'active'}
                  onTrafficUpdate={handleTrafficUpdate}
                />
              </TabsContent>
            </Tabs>

            {/* Action Bar */}
            <div className="flex items-center justify-end gap-2 pt-4">
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                onClick={() => setEmbedModalOpen(true)}
              >
                <Copy className="h-4 w-4" /> {" "}{translateInterfaceText("Copiar código")}</Button>
              <Button 
                variant="destructive"
                onClick={() => {
                  if (abTest) {
                    handleFinalizeTeste(abTest.id);
                  }
                }}
              >
                {translateInterfaceText("Finalizar Teste")}</Button>
            </div>
            
            <CopyCodeModal
              isOpen={embedModalOpen}
              onClose={() => setEmbedModalOpen(false)}
              entityType="ab-test"
              entityId={abTest.id}
              entityName={abTest.name}
              iframeCode={`<iframe src="https://app.hostvsl.com/embed/ab-test/${abTest.id}" width="640" height="360" frameborder="0" allowfullscreen></iframe>`}
              jsCode={`<script src="https://cdn.hostvsl.com/ab.js"></script>
<div id="hostvsl-ab-test" data-test="${abTest.id}"></div>
<script>
  HostVSL_AB.mount('#hostvsl-ab-test');
</script>`}
            />
          </>
        )}
      </div>
    </Layout>
  );
};

export default ABTestDetails;
